import { type ChangeEvent, forwardRef } from 'react'

import { cx } from '../../cx'
import { uiTestId } from '../../test-ids'

export type SelectProps = {
  onChange?: (e: ChangeEvent<HTMLSelectElement>) => void
  value?: string
  name?: string
  info?: React.ReactNode
  error?: string | false
  options: { label: string; value: string }[]
  label: string
  default?: string
  required?: boolean
  className?: string
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  ({ label, error, info, options, value, className, ...rest }, ref) => {
    return (
      <div className="relative w-full">
        <select
          className={cx(
            'peer w-full rounded-lg border border-primary/30 px-3 pt-[21px] pb-[5px] font-body-copy',
            error ? 'border-error bg-rose' : '',
            className
          )}
          ref={ref}
          value={value || rest.default}
          aria-invalid={!!error}
          {...rest}
        >
          {options.map((option, idx) => (
            <option key={idx} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <label
          className={cx(
            'pointer-events-none absolute top-[5px] left-0.5 ml-px pl-2.5 font-body-copy text-[12px]'
          )}
        >
          {label}
          {rest.required && '*'}
        </label>
        {error && (
          <div data-testid={uiTestId('Select.error')} className="mt-2 font-base-copy text-error">
            {error}
          </div>
        )}
        {info && <div className="mt-2 font-base-copy text-primary/60">{info}</div>}
      </div>
    )
  }
)

Select.displayName = 'Select'
